import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Zap, Loader2, ExternalLink, Copy, QrCode,
  CheckCircle, AlertCircle, RefreshCw, X
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

/**
 * AsaasPaymentModal — Modal de envio e acompanhamento de cobrança via Asaas.
 * 
 * Props:
 *   payment   — objeto de pagamento do sistema
 *   clientName — nome do cliente
 *   isOpen    — boolean
 *   onClose   — callback
 *   onSuccess — callback após enviar/atualizar
 */
export default function AsaasPaymentModal({ payment, clientName, isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState('confirm'); // confirm | sending | sent | error
  const [result, setResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const alreadySent = !!payment?.asaas_payment_id;

  // Formatar valor
  const formatCurrency = (val) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

  // Formatar data
  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  // Copiar para área de transferência
  const copyToClipboard = (text, label = 'Copiado!') => {
    navigator.clipboard.writeText(text).then(() => toast.success(label));
  };

  // Enviar cobrança
  const handleSend = async () => {
    setStep('sending');
    setErrorMessage('');
    try {
      const { data } = await api.post(`/payments/${payment.id}/send-asaas`);
      setResult(data);
      setStep('sent');
      toast.success('Cobrança gerada com sucesso no Asaas!');
      onSuccess?.();
    } catch (err) {
      const msg = err.response?.data?.detail;
      setErrorMessage(typeof msg === 'string' ? msg : 'Ocorreu um erro desconhecido ao tentar enviar a cobrança.');
      setStep('error');
    }
  };

  // Sincronizar status
  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.get(`/payments/${payment.id}/asaas-status`);
      toast.success(`Status da Cobrança: ${data.asaas_status}`);
      onSuccess?.();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Erro ao sincronizar status da cobrança');
    } finally {
      setSyncing(false);
    }
  };

  // Cancelar cobrança no Asaas
  const handleCancel = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar definitivamente esta cobrança lá no Asaas?')) return;
    setCancelLoading(true);
    try {
      await api.delete(`/payments/${payment.id}/asaas-cancel`);
      toast.success('Cobrança cancelada no Asaas com sucesso!');
      onSuccess?.();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Erro ao tentar cancelar a cobrança.');
    } finally {
      setCancelLoading(false);
    }
  };

  const invoiceUrl = result?.invoice_url || payment?.asaas_invoice_url;
  const pixKey = result?.pix_key || payment?.asaas_pix_key;
  const pixQrCode = result?.pix_qr_code || payment?.asaas_pix_qr_code;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-white border border-slate-200">
        <DialogHeader className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
            <div className="flex items-center justify-center w-8 h-8 rounded-md bg-violet-100 text-violet-600">
              <Zap size={16} className="fill-violet-200" />
            </div>
            Cobrar via Asaas
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 space-y-6">
          
          {/* Info do pagamento (Sempre visível) */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Cliente</span>
              <span className="font-semibold text-slate-700">{clientName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Valor</span>
              <span className="font-bold text-violet-700 text-base">{formatCurrency(payment?.value)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Vencimento</span>
              <span className="font-medium text-slate-700">{formatDate(payment?.due_date)}</span>
            </div>
            {payment?.description && (
              <div className="flex justify-between items-center pt-2 border-t border-slate-200 mt-2">
                <span className="text-slate-500 text-xs">Descrição</span>
                <span className="text-xs text-slate-600 text-right max-w-[200px] truncate">{payment.description}</span>
              </div>
            )}
          </div>

          {/* === JÁ ENVIADO — mostrar banner verde === */}
          {alreadySent && step !== 'sent' && (
            <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle size={18} className="text-emerald-600" />
                <span className="text-sm font-medium text-emerald-800">Cobrança Emitida</span>
              </div>
              {payment?.asaas_status && (
                <Badge variant="outline" className="bg-white text-emerald-700 border-emerald-200 uppercase text-[10px]">
                  {payment.asaas_status}
                </Badge>
              )}
            </div>
          )}

          {/* === Link de pagamento (após envio ou já enviado) === */}
          {(step === 'sent' || alreadySent) && invoiceUrl && (
            <div className="space-y-4">
              <div className="space-y-2">
                 <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Link da Fatura</Label>
                 <div className="flex items-center gap-2">
                    <Input readOnly value={invoiceUrl} className="bg-slate-50 font-mono text-xs text-slate-600 flex-1" />
                    <Button variant="outline" size="icon" onClick={() => copyToClipboard(invoiceUrl, 'Link Fatura copiado!')}>
                      <Copy size={14} className="text-slate-600" />
                    </Button>
                    <a href={invoiceUrl} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="icon">
                        <ExternalLink size={14} className="text-violet-600" />
                      </Button>
                    </a>
                 </div>
              </div>

              {pixKey && (
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1">
                    <QrCode size={14} /> PIX Copia e Cola
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={pixKey} className="bg-slate-50 font-mono text-xs text-slate-600 flex-1 truncate" />
                    <Button variant="outline" size="icon" onClick={() => copyToClipboard(pixKey, 'Pix Copia e Cola copiado!')}>
                      <Copy size={14} className="text-slate-600" />
                    </Button>
                  </div>
                </div>
              )}

              {pixQrCode && (
                <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col items-center justify-center">
                   <p className="text-xs text-slate-500 mb-2 font-medium">QR Code Pix</p>
                   <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
                      <img src={`data:image/png;base64,${pixQrCode}`} alt="QR Code Pix" className="w-32 h-32" />
                   </div>
                </div>
              )}
            </div>
          )}

          {/* === ESTADO: Confirmação inicial === */}
          {!alreadySent && step === 'confirm' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 text-center px-4">
                Ao prosseguir, uma cobrança será gerada lá no Asaas. Seu cliente poderá escolher pagar via <strong className="font-semibold text-slate-800">PIX, Boleto ou Cartão</strong> diretamente pela fatura online.
              </p>
            </div>
          )}

          {/* === ESTADO: Enviando === */}
          {step === 'sending' && (
            <div className="flex flex-col items-center justify-center py-6 text-slate-500">
              <Loader2 size={32} className="animate-spin mb-3 text-violet-600" />
              <p className="text-sm font-medium animate-pulse">Gerando cobrança no Asaas...</p>
            </div>
          )}

          {/* === ESTADO: Erro === */}
          {step === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex flex-col items-center text-center space-y-3">
              <AlertCircle size={28} className="text-red-500" />
              <div>
                <p className="text-sm font-bold text-red-800">Falha ao emitir cobrança</p>
                <p className="text-xs text-red-600 mt-1 max-w-[300px] leading-relaxed">{errorMessage}</p>
              </div>
            </div>
          )}

        </div>

        {/* Footer actions */}
        <div className="bg-slate-50 border-t border-slate-200 p-4 flex items-center justify-end gap-3 rounded-b-xl">
           {!alreadySent && step === 'confirm' && (
             <>
               <Button variant="ghost" onClick={onClose} className="text-slate-600">
                 Cancelar
               </Button>
               <Button onClick={handleSend} className="bg-violet-600 hover:bg-violet-700 text-white">
                 <Zap size={14} className="mr-2 fill-white" /> Gerar Cobrança Agora
               </Button>
             </>
           )}

           {step === 'error' && (
             <>
                <Button variant="outline" onClick={onClose}>Fechar</Button>
                <Button onClick={() => setStep('confirm')} variant="default" className="bg-slate-800 hover:bg-slate-900">
                  Voltar e Tentar Novamente
                </Button>
             </>
           )}

           {alreadySent && step !== 'sent' && (
             <div className="flex items-center w-full justify-between">
                <Button variant="ghost" size="sm" onClick={handleCancel} disabled={cancelLoading} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  {cancelLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : <X size={14} className="mr-1" />} 
                  {cancelLoading ? 'Cancelando...' : 'Cancelar Fatura'}
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
                    {syncing ? <Loader2 size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
                    Sincronizar
                  </Button>
                  <Button variant="default" size="sm" onClick={onClose}>Fechar</Button>
                </div>
             </div>
           )}

           {step === 'sent' && (
             <Button onClick={onClose} className="w-full bg-violet-600 hover:bg-violet-700">
               Concluído
             </Button>
           )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
